import React from "react";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import ButtonV2 from "../button/v2";

interface ManagementModalProps {
  title: string;
  subtitle?: string;
  subtitleClassName?: string;
  children: React.ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  onSubmit: () => void;
  onCancel?: () => void;
  onBack?: () => void;
  onClose: () => void;
  submitEnabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

const ManagementModal: React.FC<ManagementModalProps> = ({
  title,
  subtitle,
  subtitleClassName,
  children,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  onSubmit,
  onCancel,
  onBack,
  onClose,
  submitEnabled = true,
  isLoading = false,
  className,
}) => {
  const handleCancel = onCancel ?? onClose;

  return (
    <div
      className="
        fixed inset-0 z-50
        flex items-center justify-center
        bg-[rgba(10,10,10,0.20)]
      "
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          `
            relative

            w-[608px]
            max-h-[90vh]
            flex flex-col
            overflow-hidden

            rounded-[10px]
            border border-[#E5E5E5]
            bg-white

            p-6

            shadow-[
              0_10px_15px_-3px_rgba(0,0,0,0.10),
              0_4px_6px_-4px_rgba(0,0,0,0.10)
            ]
          `,
          className,
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="
            absolute
            right-4
            top-6

            flex
            h-4
            w-4
            items-center
            justify-center
            z-10
          "
        >
          <X
            size={16}
            className="
              relative
              -top-px
              text-[#0A0A0A]
              opacity-70
            "
          />
        </button>

        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <div className="flex w-full items-center gap-2">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="
                    flex
                h-4
                w-4
                    items-center
                    justify-center
                text-[#737373]
                  "
                >
              <ArrowLeft size={16} />
                </button>
              )}

              <h2
                className="
                  text-[18px]
                  font-semibold
                  leading-[18px]
                  text-[#0A0A0A]
                "
              >
                {title}
              </h2>
            </div>

            {subtitle && (
              <p
                className={cn(
                  `
                    text-[14px]
                    font-normal
                    leading-5
                    text-[#737373]
                  `,
                  onBack ? "pl-6" : "pl-0",
                  subtitleClassName,
                )}
              >
                {subtitle}
              </p>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {children}
          </div>

          <div
            className="
              flex
              justify-end
              gap-3
              mt-auto
            "
          >
            <ButtonV2
              variant="outline"
              size="md"
              onClick={handleCancel}
              disabled={isLoading}
              className="
                h-9
                rounded-[8px]
                border-[#E5E5E5]
              "
            >
              {cancelLabel}
            </ButtonV2>

            <ButtonV2
              variant="primary"
              size="md"
              onClick={onSubmit}
              loading={isLoading}
              disabled={!submitEnabled}
              className="
                h-9
                rounded-[8px]
              "
            >
              {submitLabel}
            </ButtonV2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagementModal;